import { useState, useRef, useEffect, useCallback } from 'react';
import { useNotifications } from '@medprompt/shared';
import { ConsultationStatusEnum } from '../enums/consultationStatusEnum';
import AudioRecorderService, { MicInterruptReason } from '../services/AudioRecorderService';
import MicrophoneCheckService from '../services/MicrophoneCheckService';
import ScreenWakeLockService from '../services/ScreenWakeLockService';
import {
  defaultDurationWarningsShown,
  loadRecordingSession,
  saveRecordingSession,
  clearRecordingSession,
  mergePendingSegments,
  prunePendingSegment,
  readPendingMicDevice,
} from './recordingSession';
import { RECORDING_WARN_15_S, RECORDING_WARN_25_S, RECORDING_MAX_S } from './recordingLimits';

/**
 * Live capture for a consultation in RECORDING: the recorder lifecycle, pause / mic recovery,
 * the pending segment upload queue, the duration limits, and finalizing once capture stops.
 */
const useConsultationRecording = ({
  consultationId,
  consultationStatus,
  consultationService,
  reloadConsultation,
  navigate,
}) => {
  const { addNotification } = useNotifications();

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [micInterruptReason, setMicInterruptReason] = useState(null);
  const [recoveryDeviceId, setRecoveryDeviceId] = useState(null);
  const [micResumeLoading, setMicResumeLoading] = useState(false);
  const [segmentQueue, setSegmentQueue] = useState([]);
  const [showUploadFailure, setShowUploadFailure] = useState(false);
  const [storageQuotaExceeded, setStorageQuotaExceeded] = useState(false);
  const [uploadRetryLoading, setUploadRetryLoading] = useState(false);
  const [finalizePending, setFinalizePending] = useState(false);
  const [finalizeTotalSegments, setFinalizeTotalSegments] = useState(null);
  const [finalizeLoading, setFinalizeLoading] = useState(false);
  const [finalizeError, setFinalizeError] = useState(null);
  const recorderRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const initializingRecordingRef = useRef(false);
  const recordingConsultationIdRef = useRef(null);
  const micInterruptReasonRef = useRef(null);
  const segmentQueueRef = useRef([]);
  const finalizePendingRef = useRef(false);
  const finalizeTotalSegmentsRef = useRef(null);
  const durationWarningsShownRef = useRef(defaultDurationWarningsShown());
  const durationCapTriggeredRef = useRef(false);
  const handleStopRecordingRef = useRef(null);
  const wakeLockServiceRef = useRef(null);
  const recordingInitGenerationRef = useRef(0);
  if (!wakeLockServiceRef.current) {
    wakeLockServiceRef.current = new ScreenWakeLockService();
  }

  const addNotificationRef = useRef(addNotification);
  const navigateRef = useRef(navigate);
  const consultationServiceRef = useRef(consultationService);
  useEffect(() => { addNotificationRef.current = addNotification; }, [addNotification]);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);
  useEffect(() => { consultationServiceRef.current = consultationService; }, [consultationService]);

  const isRecording = consultationStatus === ConsultationStatusEnum.RECORDING.value;

  useEffect(() => {
    if (!isRecording) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = finalizePendingRef.current
        ? 'You have a recording that still needs uploading. Are you sure you want to leave?'
        : 'You have an active recording. Are you sure you want to leave?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isRecording]);

  /** True when it is fine to leave this consultation (asks first while recording). */
  const confirmLeaveRecording = useCallback(() => {
    if (!isRecording) return true;
    const message = finalizePendingRef.current
      ? 'You have a recording that still needs uploading. Are you sure you want to leave?'
      : 'You have an active recording. Leaving will pause it. Are you sure you want to leave?';
    return window.confirm(message);
  }, [isRecording]);

  const syncSegmentQueue = useCallback((nextQueue) => {
    const merged = mergePendingSegments([], nextQueue);
    segmentQueueRef.current = merged;
    setSegmentQueue(merged);
    // Only clear the banner here; failures set showUploadFailure explicitly so we do not
    // flash it for every in-flight segment that is about to upload.
    if (finalizePendingRef.current || merged.length === 0) {
      setShowUploadFailure(false);
    }
    return merged;
  }, []);

  const buildSessionSnapshot = useCallback((extras = {}) => {
    const existing = consultationId ? loadRecordingSession(consultationId) : null;
    const recorderSnapshot = recorderRef.current?.getSessionSnapshot?.() || {};
    return {
      ...(existing || {}),
      ...recorderSnapshot,
      pauseReason: micInterruptReasonRef.current
        || recorderSnapshot.pauseReason
        || existing?.pauseReason
        || null,
      pendingSegments: segmentQueueRef.current,
      finalizePending: finalizePendingRef.current,
      totalSegments: finalizeTotalSegmentsRef.current ?? existing?.totalSegments ?? null,
      durationWarningsShown: durationWarningsShownRef.current,
      storageQuotaExceeded: false,
      ...extras
    };
  }, [consultationId]);

  const persistRecordingSession = useCallback((extras = {}, options = {}) => {
    if (!consultationId) return { ok: false };
    const includePendingAudio = options.includePendingAudio !== false;
    const snapshot = buildSessionSnapshot(extras);
    if (!includePendingAudio) {
      const existing = loadRecordingSession(consultationId);
      snapshot.pendingSegments = existing?.pendingSegments || [];
      if (existing?.storageQuotaExceeded) {
        snapshot.storageQuotaExceeded = true;
      }
    }
    const result = saveRecordingSession(consultationId, snapshot);
    if (result.quotaExceeded) {
      setStorageQuotaExceeded(true);
    } else if (result.ok && includePendingAudio) {
      setStorageQuotaExceeded(false);
    }
    return result;
  }, [consultationId, buildSessionSnapshot]);

  const syncSegmentQueueRef = useRef(syncSegmentQueue);
  const persistRecordingSessionRef = useRef(persistRecordingSession);
  useEffect(() => { syncSegmentQueueRef.current = syncSegmentQueue; }, [syncSegmentQueue]);
  useEffect(() => { persistRecordingSessionRef.current = persistRecordingSession; }, [persistRecordingSession]);

  useEffect(() => {
    const initGeneration = recordingInitGenerationRef.current;
    const isStaleInit = () => recordingInitGenerationRef.current !== initGeneration;

    const handleMicInterrupted = (reason) => {
      micInterruptReasonRef.current = reason;
      setMicInterruptReason(reason);
      setIsPaused(true);
      setMicLevel(0);
      wakeLockServiceRef.current?.release();
      persistRecordingSessionRef.current({ pauseReason: reason });
    };

    const handleVisibilityChange = () => {
      if (finalizePendingRef.current) return;
      const recorder = recorderRef.current;
      if (!recorder?.isRecording()) return;

      if (document.visibilityState === 'hidden') {
        // Keep capturing across tab switch / minimise; freeze silence so throttled
        // timers cannot false-trigger SILENCE. Screen lock is handled via mute /
        // AudioContext interrupted, not via hide itself.
        recorder.freezeSilenceDetection();
        return;
      }

      const finishBecameVisible = async () => {
        if (recorderRef.current !== recorder) return;
        // Never auto-resume an existing interrupt; upgrade to DISCONNECT if the mic died
        if (micInterruptReasonRef.current || recorder.isPaused()) {
          recorder.unfreezeSilenceDetection();
          if (!recorder.isCaptureHealthy()) {
            recorder.triggerInterrupt(MicInterruptReason.DISCONNECT);
          }
          return;
        }
        await recorder.resumeIfSuspended();
        if (recorderRef.current !== recorder) return;
        recorder.unfreezeSilenceDetection();
        wakeLockServiceRef.current?.reacquireIfDesired();
        if (!recorder.isCaptureHealthy()) {
          recorder.triggerInterrupt(MicInterruptReason.DISCONNECT);
        }
      };
      finishBecameVisible();
    };

    const initRecording = async () => {
      if (consultationStatus !== ConsultationStatusEnum.RECORDING.value) return;

      // CRITICAL: If already recording this same consultation, don't reinitialize
      if (recordingConsultationIdRef.current === consultationId) return;
      if (recorderRef.current?.isRecording()) return;
      if (initializingRecordingRef.current) return;

      const savedSession = loadRecordingSession(consultationId);

      durationWarningsShownRef.current = {
        ...defaultDurationWarningsShown(),
        ...(savedSession?.durationWarningsShown || {})
      };
      durationCapTriggeredRef.current = false;

      // Capture already stopped; waiting for user to upload & complete
      if (savedSession?.finalizePending) {
        recordingConsultationIdRef.current = consultationId;
        const pending = mergePendingSegments([], savedSession.pendingSegments || []);
        syncSegmentQueueRef.current(pending);
        finalizePendingRef.current = true;
        finalizeTotalSegmentsRef.current = typeof savedSession.totalSegments === 'number'
          ? savedSession.totalSegments
          : null;
        setFinalizePending(true);
        setFinalizeTotalSegments(finalizeTotalSegmentsRef.current);
        setFinalizeError(null);
        if (savedSession.elapsedMs) {
          setElapsedSeconds(Math.floor(savedSession.elapsedMs / 1000));
        }
        if (savedSession.storageQuotaExceeded) {
          setStorageQuotaExceeded(true);
        }
        setShowUploadFailure(false);
        wakeLockServiceRef.current?.release();
        return;
      }

      // Remount after active elapsed already at/over the hard cap — skip restarting capture
      const restoredElapsedS = Math.floor((savedSession?.elapsedMs || 0) / 1000);
      if (restoredElapsedS >= RECORDING_MAX_S) {
        recordingConsultationIdRef.current = consultationId;
        const pending = mergePendingSegments([], savedSession?.pendingSegments || []);
        syncSegmentQueueRef.current(pending);
        finalizePendingRef.current = true;
        durationCapTriggeredRef.current = true;
        const totalSegments = typeof savedSession?.segmentIndex === 'number'
          ? savedSession.segmentIndex
          : (typeof savedSession?.totalSegments === 'number' ? savedSession.totalSegments : pending.length);
        finalizeTotalSegmentsRef.current = totalSegments;
        setElapsedSeconds(restoredElapsedS);
        setFinalizePending(true);
        setFinalizeTotalSegments(totalSegments);
        setFinalizeError(null);
        setShowUploadFailure(false);
        wakeLockServiceRef.current?.release();
        persistRecordingSessionRef.current({
          finalizePending: true,
          totalSegments,
          pendingSegments: pending,
          elapsedMs: restoredElapsedS * 1000,
          durationWarningsShown: {
            warn15: true,
            warn25: true
          }
        });
        addNotificationRef.current(
          'recording-limit-reached',
          'Recording stopped: 30-minute limit reached.'
        );
        return;
      }

      initializingRecordingRef.current = true;
      recordingConsultationIdRef.current = consultationId;

      if (recorderRef.current) {
        recorderRef.current.stopRecording();
        recorderRef.current = null;
      }

      // Restore any unsynced segments from a previous tab lifetime
      if (savedSession?.pendingSegments?.length) {
        syncSegmentQueueRef.current(savedSession.pendingSegments);
        setShowUploadFailure(true);
      } else {
        syncSegmentQueueRef.current([]);
      }
      if (savedSession?.storageQuotaExceeded) {
        setStorageQuotaExceeded(true);
      }

      const pendingDeviceId = readPendingMicDevice();
      const deviceId = pendingDeviceId
        || savedSession?.deviceId
        || MicrophoneCheckService.getLastDeviceId();

      const micStatus = await MicrophoneCheckService.checkMicrophoneStatus();
      if (isStaleInit()) return;
      if (!micStatus.canRecord) {
        initializingRecordingRef.current = false;
        recordingConsultationIdRef.current = null;
        addNotificationRef.current('mic-check-failed', micStatus.message, { isError: true });
        navigateRef.current('/', { state: { selectTab: 'scribe' } });
        return;
      }

      const recorder = new AudioRecorderService();
      recorderRef.current = recorder;

      const restore = savedSession
        ? {
            segmentIndex: typeof savedSession.segmentIndex === 'number' ? savedSession.segmentIndex : 0,
            elapsedMs: savedSession.elapsedMs || 0,
            pausedDuration: savedSession.pausedDuration || 0,
            deviceId: deviceId || savedSession.deviceId
          }
        : null;

      const hasPermission = await recorder.requestPermission(deviceId);
      if (isStaleInit()) {
        recorder.cleanup();
        if (recorderRef.current === recorder) recorderRef.current = null;
        initializingRecordingRef.current = false;
        return;
      }
      if (!hasPermission) {
        initializingRecordingRef.current = false;
        recordingConsultationIdRef.current = null;
        recorderRef.current = null;
        const detailedStatus = await MicrophoneCheckService.checkMicrophoneStatus();
        addNotificationRef.current('mic-permission-denied', detailedStatus.message, { isError: true });
        navigateRef.current('/', { state: { selectTab: 'scribe' } });
        return;
      }

      if (recorderRef.current !== recorder) {
        recorder.cleanup();
        initializingRecordingRef.current = false;
        recordingConsultationIdRef.current = null;
        return;
      }

      const started = await recorder.startRecording(
        async (segmentInfo) => {
          const withPending = mergePendingSegments(segmentQueueRef.current, [segmentInfo]);
          syncSegmentQueueRef.current(withPending);
          persistRecordingSessionRef.current({ pendingSegments: withPending });

          try {
            await consultationServiceRef.current.uploadSegment(
              consultationId,
              segmentInfo.index,
              segmentInfo.data
            );
            const pruned = prunePendingSegment(segmentQueueRef.current, segmentInfo.index);
            syncSegmentQueueRef.current(pruned);
            persistRecordingSessionRef.current({ pendingSegments: pruned });
          } catch (err) {
            console.error(`Segment ${segmentInfo.index} upload failed:`, err);
            if (!finalizePendingRef.current) {
              setShowUploadFailure(true);
            }
            persistRecordingSessionRef.current({ pendingSegments: segmentQueueRef.current });
          }
        },
        {
          deviceId: recorder.getDeviceId() || deviceId,
          restore: restore || { deviceId: recorder.getDeviceId() || deviceId },
          onMicInterrupted: handleMicInterrupted,
          onLevel: (rms) => setMicLevel(rms)
        }
      );

      initializingRecordingRef.current = false;

      if (isStaleInit()) {
        recorder.stopRecording();
        if (recorderRef.current === recorder) recorderRef.current = null;
        return;
      }

      if (!started) {
        recorderRef.current = null;
        recordingConsultationIdRef.current = null;
        addNotificationRef.current('recording-start-failed', 'Failed to start recording', { isError: true });
        return;
      }

      if (savedSession?.elapsedMs) {
        setElapsedSeconds(Math.floor(savedSession.elapsedMs / 1000));
      }

      // Restore interrupt / pause UI after reload (in-app leave, disconnect, silence)
      if (savedSession?.pauseReason) {
        micInterruptReasonRef.current = savedSession.pauseReason;
        setMicInterruptReason(savedSession.pauseReason);
        setIsPaused(true);
        recorder.pauseForInterrupt(savedSession.pauseReason);
        wakeLockServiceRef.current?.release();
        if ((savedSession.pauseReason === MicInterruptReason.BACKGROUND
              || savedSession.pauseReason === MicInterruptReason.SILENCE)
            && !recorder.isCaptureHealthy()) {
          recorder.triggerInterrupt(MicInterruptReason.DISCONNECT);
        }
      } else if (savedSession?.isPaused) {
        setIsPaused(true);
        recorder.pauseRecording();
        wakeLockServiceRef.current?.release();
      } else {
        wakeLockServiceRef.current?.acquire();
      }

      setRecoveryDeviceId(recorder.getDeviceId() || deviceId || null);
      persistRecordingSessionRef.current();

      const applyDurationLimits = (elapsed) => {
        if (finalizePendingRef.current || durationCapTriggeredRef.current) return;

        const warnings = durationWarningsShownRef.current;
        let warningsChanged = false;

        if (elapsed >= RECORDING_WARN_15_S && !warnings.warn15) {
          warnings.warn15 = true;
          warningsChanged = true;
          addNotificationRef.current(
            'recording-warn-15',
            '15 minutes remaining before the 30-minute recording limit.'
          );
        }

        if (elapsed >= RECORDING_WARN_25_S && !warnings.warn25) {
          warnings.warn25 = true;
          warningsChanged = true;
          addNotificationRef.current(
            'recording-warn-25',
            '5 minutes remaining before the 30-minute recording limit.'
          );
        }

        if (warningsChanged) {
          durationWarningsShownRef.current = { ...warnings };
          persistRecordingSessionRef.current(
            { durationWarningsShown: durationWarningsShownRef.current },
            { includePendingAudio: false }
          );
        }

        if (elapsed >= RECORDING_MAX_S) {
          durationCapTriggeredRef.current = true;
          addNotificationRef.current(
            'recording-limit-reached',
            'Recording stopped: 30-minute limit reached.'
          );
          handleStopRecordingRef.current?.();
        }
      };

      // Catch up warnings / cap if remounted mid-session past a threshold
      applyDurationLimits(recorder.getElapsedTime());

      elapsedTimerRef.current = setInterval(() => {
        if (recorderRef.current) {
          const elapsed = recorderRef.current.getElapsedTime();
          setElapsedSeconds(elapsed);
          applyDurationLimits(elapsed);
          persistRecordingSessionRef.current({}, { includePendingAudio: false });
        }
      }, 1000);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    initRecording();

    return () => {
      recordingInitGenerationRef.current += 1;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      wakeLockServiceRef.current?.release();

      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }

      // In-app leave: pause (do not finalize) so remount restores paused and requires Resume
      if (consultationId && (recorderRef.current?.isRecording() || finalizePendingRef.current)) {
        try {
          if (recorderRef.current?.isRecording()) {
            const pauseReason = micInterruptReasonRef.current || MicInterruptReason.BACKGROUND;
            micInterruptReasonRef.current = pauseReason;
            const { totalSegments, finalSegment } = recorderRef.current.stopRecording();
            recorderRef.current = null;
            if (finalSegment) {
              segmentQueueRef.current = mergePendingSegments(segmentQueueRef.current, [finalSegment]);
            }
            persistRecordingSessionRef.current({
              segmentIndex: totalSegments,
              pendingSegments: segmentQueueRef.current,
              finalizePending: false,
              isPaused: true,
              pauseReason
            });
          } else {
            persistRecordingSessionRef.current({
              finalizePending: true,
              totalSegments: finalizeTotalSegmentsRef.current,
              pendingSegments: segmentQueueRef.current
            });
          }
        } catch {
          // ignore
        }
      }

      recordingConsultationIdRef.current = null;
      initializingRecordingRef.current = false;
    };
  }, [consultationStatus, consultationId]);

  const handlePauseToggle = useCallback(async () => {
    if (!recorderRef.current) return;
    // Mic interrupt recovery uses the banner Resume button
    if (micInterruptReasonRef.current) return;

    if (isPaused) {
      const selectedId = recoveryDeviceId || MicrophoneCheckService.getLastDeviceId();
      const currentId = recorderRef.current.getDeviceId();
      const switchTo = selectedId && selectedId !== currentId ? selectedId : null;
      const ok = await recorderRef.current.resumeRecording(switchTo);
      if (ok) {
        setIsPaused(false);
        setMicInterruptReason(null);
        if (recorderRef.current.getDeviceId()) {
          setRecoveryDeviceId(recorderRef.current.getDeviceId());
        }
        wakeLockServiceRef.current?.acquire();
      } else {
        addNotification(
          'mic-resume-failed',
          'Could not resume with the selected microphone. Please try another device.',
          { isError: true }
        );
      }
    } else {
      recorderRef.current.pauseRecording();
      setIsPaused(true);
      wakeLockServiceRef.current?.release();
    }

    persistRecordingSession();
  }, [isPaused, recoveryDeviceId, addNotification, persistRecordingSession]);

  const handleSelectRecordingMic = useCallback((deviceId) => {
    if (!deviceId) return;
    setRecoveryDeviceId(deviceId);
    MicrophoneCheckService.setLastDeviceId(deviceId);
    persistRecordingSession({ deviceId });
  }, [persistRecordingSession]);

  const handleMicRecoveryReady = useCallback(({ deviceId }) => {
    if (deviceId) {
      setRecoveryDeviceId(deviceId);
      MicrophoneCheckService.setLastDeviceId(deviceId);
    }
  }, []);

  const handleMicResume = useCallback(async () => {
    if (!recorderRef.current) return;
    setMicResumeLoading(true);
    try {
      const deviceId = recoveryDeviceId || MicrophoneCheckService.getLastDeviceId();
      const ok = await recorderRef.current.resumeRecording(deviceId);
      if (!ok) {
        addNotification(
          'mic-resume-failed',
          'Could not resume with the selected microphone. Please try another device.',
          { isError: true }
        );
        return;
      }
      micInterruptReasonRef.current = null;
      setMicInterruptReason(null);
      setIsPaused(false);
      wakeLockServiceRef.current?.acquire();
      persistRecordingSession({ pauseReason: null });
    } finally {
      setMicResumeLoading(false);
    }
  }, [recoveryDeviceId, addNotification, persistRecordingSession]);

  const uploadPendingSegments = useCallback(async () => {
    const pending = [...segmentQueueRef.current];
    const stillPending = [];

    for (const segment of pending) {
      try {
        await consultationService.uploadSegment(consultationId, segment.index, segment.data);
      } catch (err) {
        console.error(`Segment ${segment.index} upload failed:`, err);
        stillPending.push(segment);
      }
    }

    syncSegmentQueue(stillPending);
    persistRecordingSession({ pendingSegments: stillPending });
    return stillPending;
  }, [consultationId, consultationService, syncSegmentQueue, persistRecordingSession]);

  const handleRetryPendingUploads = useCallback(async () => {
    if (segmentQueueRef.current.length === 0) {
      setShowUploadFailure(false);
      return;
    }
    setUploadRetryLoading(true);
    try {
      const stillPending = await uploadPendingSegments();
      if (stillPending.length === 0) {
        setShowUploadFailure(false);
        addNotification('segment-upload-retry-ok', 'Pending segments uploaded successfully');
      } else {
        setShowUploadFailure(true);
        addNotification(
          'segment-upload-retry-partial',
          `${stillPending.length} segment${stillPending.length === 1 ? '' : 's'} still failed to upload`,
          { isError: true }
        );
      }
    } finally {
      setUploadRetryLoading(false);
    }
  }, [uploadPendingSegments, addNotification]);

  const finishRecording = useCallback(async () => {
    setFinalizeLoading(true);
    setFinalizeError(null);
    try {
      const totalSegments = finalizeTotalSegmentsRef.current ?? (
        segmentQueueRef.current.reduce((max, segment) => Math.max(max, segment.index + 1), 0)
      );

      const revealFinalizePanel = (errorMessage) => {
        finalizePendingRef.current = true;
        setFinalizePending(true);
        persistRecordingSession({
          finalizePending: true,
          totalSegments,
          pendingSegments: segmentQueueRef.current
        });
        if (errorMessage) setFinalizeError(errorMessage);
      };

      let stillPending = await uploadPendingSegments();
      if (stillPending.length > 0) {
        revealFinalizePanel(
          `Could not upload ${stillPending.length} segment${stillPending.length === 1 ? '' : 's'}. Check your connection and try again.`
        );
        return;
      }

      const missingIndices = await consultationService.getMissingSegments(consultationId, totalSegments);
      if (missingIndices.length > 0) {
        const byIndex = new Map(segmentQueueRef.current.map((segment) => [segment.index, segment]));
        const unresolved = [];
        for (const index of missingIndices) {
          const segment = byIndex.get(index);
          if (!segment) {
            unresolved.push(index);
            continue;
          }
          try {
            await consultationService.uploadSegment(consultationId, index, segment.data);
          } catch (err) {
            console.error(`Missing segment ${index} upload failed:`, err);
            unresolved.push(index);
            stillPending = mergePendingSegments(stillPending, [segment]);
          }
        }

        if (unresolved.length > 0) {
          syncSegmentQueue(stillPending);
          revealFinalizePanel(
            `Could not upload ${unresolved.length} segment${unresolved.length === 1 ? '' : 's'}. Check your connection and try again.`
          );
          return;
        }
      }

      await consultationService.completeRecording(consultationId, totalSegments);

      clearRecordingSession(consultationId);
      finalizePendingRef.current = false;
      finalizeTotalSegmentsRef.current = null;
      syncSegmentQueue([]);
      setFinalizePending(false);
      setFinalizeTotalSegments(null);
      setShowUploadFailure(false);
      setStorageQuotaExceeded(false);
      recordingConsultationIdRef.current = null;
      addNotification('recording-complete', 'Recording completed. Processing...');
      await reloadConsultation();
    } catch (err) {
      console.error('Error completing recording:', err);
      finalizePendingRef.current = true;
      setFinalizePending(true);
      setFinalizeError('Failed to complete recording. Check your connection and try again.');
      addNotification('recording-complete-error', 'Failed to complete recording', { isError: true });
      persistRecordingSession({
        finalizePending: true,
        totalSegments: finalizeTotalSegmentsRef.current,
        pendingSegments: segmentQueueRef.current
      });
    } finally {
      setFinalizeLoading(false);
    }
  }, [
    consultationId,
    consultationService,
    uploadPendingSegments,
    syncSegmentQueue,
    persistRecordingSession,
    addNotification,
    reloadConsultation
  ]);

  const handleStopRecording = useCallback(() => {
    if (!recorderRef.current) return;

    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }

    // Drop interrupt UI immediately so recovery MicrophoneMonitor releases its stream
    micInterruptReasonRef.current = null;
    setMicInterruptReason(null);
    setShowUploadFailure(false);
    setFinalizeError(null);
    wakeLockServiceRef.current?.release();

    const { totalSegments, finalSegment } = recorderRef.current.stopRecording();
    recorderRef.current = null;
    recordingConsultationIdRef.current = consultationId;
    setMicLevel(0);
    setIsPaused(false);

    let pending = segmentQueueRef.current;
    if (finalSegment) {
      pending = mergePendingSegments(pending, [finalSegment]);
      syncSegmentQueue(pending);
    }

    finalizePendingRef.current = true;
    finalizeTotalSegmentsRef.current = totalSegments;
    setFinalizeTotalSegments(totalSegments);
    setFinalizeLoading(true);

    persistRecordingSession({
      finalizePending: true,
      totalSegments,
      pendingSegments: pending,
      isPaused: false,
      pauseReason: null,
      segmentIndex: totalSegments,
      elapsedMs: elapsedSeconds * 1000,
      durationWarningsShown: durationWarningsShownRef.current
    });

    finishRecording();
  }, [consultationId, elapsedSeconds, syncSegmentQueue, persistRecordingSession, finishRecording]);

  useEffect(() => {
    handleStopRecordingRef.current = handleStopRecording;
  }, [handleStopRecording]);

  /** Stop capture and forget everything held for this consultation, e.g. before deleting it. */
  const discardRecording = useCallback(() => {
    if (recorderRef.current) {
      recorderRef.current.stopRecording();
      recorderRef.current = null;
    }
    finalizePendingRef.current = false;
    finalizeTotalSegmentsRef.current = null;
    segmentQueueRef.current = [];
    setSegmentQueue([]);
    setFinalizePending(false);
    setShowUploadFailure(false);
    clearRecordingSession(consultationId);
  }, [consultationId]);

  return {
    isRecording,
    elapsedSeconds,
    isPaused,
    micLevel,
    micInterruptReason,
    recoveryDeviceId,
    micResumeLoading,
    segmentQueue,
    showUploadFailure,
    storageQuotaExceeded,
    uploadRetryLoading,
    finalizePending,
    finalizeTotalSegments,
    finalizeLoading,
    finalizeError,
    handlePauseToggle,
    handleSelectRecordingMic,
    handleMicRecoveryReady,
    handleMicResume,
    handleRetryPendingUploads,
    handleStopRecording,
    handleUploadAndComplete: finishRecording,
    confirmLeaveRecording,
    discardRecording,
  };
};

export default useConsultationRecording;
